import { useFlag } from '@reflag/react-sdk';

import type * as api from '@gitbook/api-client';
import type { NavigationSiteSettingsSection } from '@gitbook/app-navigation';
import { checkIsCommunityProductKey, isLegacySiteType } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';

/**
 * Definition of sections and groupings for settings in the site settings tab menu / mobile palette.
 */
export const siteSettingsSections = [
    [
        {
            type: 'section',
            id: 'general',
            title: 'General',
            icon: Icon.Settings,
        },
    ],
    [
        {
            type: 'section',
            id: 'audience',
            title: 'Audience',
            icon: Icon.PublishStatusPublic,
        },
        {
            type: 'section',
            id: 'domains',
            title: 'Domain and URL',
            icon: Icon.Globe,
        },
        {
            type: 'section',
            id: 'redirects',
            title: 'Redirects',
            icon: Icon.Redirect,
        },
        {
            type: 'section',
            id: 'structure',
            title: 'Structure',
            icon: Icon.SiteStructure,
        },
    ],
    [
        {
            type: 'section',
            id: 'ai',
            title: 'AI & MCP',
            icon: Icon.MCP,
        },
        {
            type: 'section',
            id: 'embed',
            title: 'Docs Embed',
            icon: Icon.Link,
        },
        {
            type: 'section',
            id: 'legal',
            title: 'Legal',
            icon: Icon.RoleReader,
        },
    ],
    [
        {
            type: 'section',
            id: 'sponsorship',
            title: 'Ads and sponsorship',
            icon: Icon.PaidMember,
        },
        {
            type: 'section',
            id: 'plan',
            title: 'Plan',
            icon: Icon.Upgrade,
        },
    ],
] as const;

/**
 * Guard whether the settings options should be shown for a site.
 */
export function showSettingsSection(
    site: api.Site,
    organization: api.Organization
): Record<NavigationSiteSettingsSection, boolean> {
    const isNewSiteType = !isLegacySiteType(site.type);
    const isCommunity = checkIsCommunityProductKey(organization.plan);
    const isEmbedAssistantPanelEnabled = useFlag('EMBED_ASSISTANT_PANEL').isEnabled;

    return {
        general: true,
        audience: true,
        schema: !!site.adaptiveContent?.enabled,
        domains: true,
        structure: true,
        ai: true,
        embed: isEmbedAssistantPanelEnabled,
        redirects: true,
        sponsorship: isCommunity,
        legal: false,
        plan: isNewSiteType && !isCommunity,
    };
}
